import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { BACKEND_URL, resolveImage } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import Seo from "../components/site/Seo";
import Html from "../components/site/Html";

const BlogDetail = () => {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-zinc-500">Loading...</div>;
  if (notFound || !post) {
    return (
      <div className="py-32 text-center" data-testid="blog-not-found">
        <h2 className="font-display text-3xl font-bold mb-3">Post not found</h2>
        <Link to="/blog" className="text-orange-600 font-semibold">← Back to blog</Link>
      </div>
    );
  }

  const cover = resolveImage(post.cover_image);

  return (
    <article data-testid="blog-detail-page" className="py-20">
      <Seo
        title={post.seo?.title || `${post.title} — Reptile Global`}
        description={post.seo?.description || post.excerpt}
        keywords={post.seo?.keywords}
        ogImage={post.seo?.og_image || cover}
        ogType={post.seo?.og_type || "article"}
        ogUrl={post.seo?.og_url}
        twitterCard={post.seo?.twitter_card}
        canonicalUrl={post.seo?.canonical_url}
        jsonLd={post.seo?.json_ld}
        organizationSchema={settings?.organization_schema}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" className="text-sm text-zinc-500 hover:text-orange-600">← All posts</Link>
        <div className="mt-6 text-xs text-zinc-500">{new Date(post.created_at).toLocaleDateString()} · {post.author}</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950">{post.title}</h1>
        {post.excerpt && <p className="mt-5 text-lg text-zinc-600">{post.excerpt}</p>}
        {(post.tags || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {post.tags.map((t) => (
              <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="text-xs bg-zinc-100 hover:bg-orange-100 text-zinc-700 px-2 py-0.5 rounded-full">#{t}</Link>
            ))}
          </div>
        )}
        {cover && (
          <img src={cover} alt={post.title} className="mt-10 w-full rounded-2xl border border-zinc-200" />
        )}
        <Html html={post.content} className="mt-10 text-zinc-800 text-base leading-relaxed" />
      </div>
    </article>
  );
};

export default BlogDetail;
